'use client';

import { FC, useEffect, useMemo, useState } from 'react';

import { useAuthentication, User, UserGroups } from '@ravago/shared/authentication';
import { AssetUtils, isFeatureEnabled } from '@ravago/shared/next-data-access';
import { ShoppingCartUtils } from '@ravago/shared/next-data-access/client';
import { FeatureToggle } from '@ravago/shared/page-data/models/config';
import { DynamicPageContentType } from '@ravago/shared/page-data/models/contentful';
import { NormalizedBusinessContext, NormalizedNavigationDropdown } from '@ravago/shared/page-data/models/contentful-mapped';
import { Customer } from '@ravago/shared/page-data/models/features';
import { LocalizedRoutes } from '@ravago/shared/page-data/models/pages';
import { CustomerSwitchDialog } from '@ravago/shared/page-elements-radiance/components/shared/customer-switch-dialog/customer-switch-dialog';
import { useShoppingCartContext } from '@ravago/shared/page-elements-radiance/context/shopping-cart-context';
import { useConfigContext } from '@ravago/shared/page-elements-radiance/hooks/use-config-context';
import { DataTestSelector } from '@ravago/shared/page-elements-radiance/utils/format/format.utils';
import { getCountryCode, getLanguageCode } from '@ravago/shared/page-elements-radiance/utils/locale/locale.utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Flyout, FlyoutActions, FlyoutContent, FlyoutHeader } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { Header } from '@ravago/shared/radiance/components/header/components/Header/Header';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Image } from '@ravago/shared/radiance/components/image/components/Image/Image';
import { NavBar } from '@ravago/shared/radiance/components/navigation/components/NavBar/NavBar';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { TechnicalSupport } from '../../../models/contentful/tech-knowledge.model';
import { Country, Language } from '../../../services/contentful/locale.service';
import NavigationService from '../../../services/navigation/navigation.service';
import TechKnowledgeBaseDialog from '../tech-knowledge-base-dialog/tech-knowledge-base-dialog';

import AuthenticationButton from './components/authentication-button';
import CountryLanguageMenu from './components/country-language-menu';
import LoginMenu from './components/login-menu';
import MegaMenu from './components/mega-menu';
import Navigation from './components/navigation';
import ProfileMenu from './components/profile-menu';
import ShoppingCartMenu from './components/shopping-cart/shopping-cart-menu';

interface Props {
  businessContext: NormalizedBusinessContext;
  locale: string;
  availableLanguages: Array<Language>;
  availableCountries: Array<Country>;
  technicalSupport: Array<TechnicalSupport>;
  acceptWebp?: boolean;
  localizedRoutes?: LocalizedRoutes;
  features: FeatureToggle[];
  user?: User;
  accessToken?: string;
  currentCustomer: Customer | undefined;
  initialShoppingCartBadge: number;
  dynamicPageUrls?: { [key in DynamicPageContentType]?: string };
}

type AvailableMenus = 'accountMenu' | 'countryLanguageMenu' | 'loginMenu' | 'mobileMenu' | 'mobileAccountMenu' | 'shoppingCartMenu';
type AvailableDialogs = 'techKnowledgeBase' | 'customerSwitch';

const Navbar: FC<Props> = ({ businessContext, accessToken, user, features, locale, availableLanguages, availableCountries, technicalSupport, acceptWebp, localizedRoutes, currentCustomer, initialShoppingCartBadge, dynamicPageUrls }: Props) => {
  const translate = useTranslations();
  const { canAuthenticate } = useAuthentication();
  const router = useRouter();
  const queryParams = useSearchParams();
  const path = usePathname();
  const { linkLegacyDigitalPlatform, isPublic } = useConfigContext();
  const { desktop, tabLand, tabPort, phone } = useBreakpoint();
  const { state: shoppingCartState, resetCartValidation } = useShoppingCartContext();

  const defaultMenuStates: { [key in AvailableMenus]: boolean } = {
    accountMenu: false,
    countryLanguageMenu: false,
    loginMenu: false,
    mobileMenu: false,
    mobileAccountMenu: false,
    shoppingCartMenu: false
  };

  const [openMenus, setOpenMenus] = useState<{ [key in AvailableMenus]: boolean }>(defaultMenuStates);

  const defaultDialogStates: { [key in AvailableDialogs]: boolean } = {
    techKnowledgeBase: false,
    customerSwitch: false
  };

  const [openDialogs, setOpenDialogs] = useState<{ [key in AvailableDialogs]: boolean }>(defaultDialogStates);

  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [firstLoginChanged, setFirstLoginChanged] = useState(false);
  const [mobileNavStep, setMobileNavStep] = useState<'nav' | 'countryAndLanguage'>('nav');
  const [shoppingCartBadge, setShoppingCartBadge] = useState<number>(initialShoppingCartBadge);

  const [dropdownMenu, setDropdownMenu] = useState<NormalizedNavigationDropdown>();
  const [expandedState, setExpandedState] = useState<'left' | 'right' | 'none'>('none');

  const isCustomerPortal = user && user.groups.includes(UserGroups.customerPortal);

  const showLogin = useMemo(() => (linkLegacyDigitalPlatform && !isPublic && canAuthenticate) || (user && accessToken), [linkLegacyDigitalPlatform, isPublic, canAuthenticate, user, accessToken]);

  const handleHamburgerClick = () => {
    if (!openMenus.mobileMenu) setMobileNavStep('nav');
    setOpenMenus({ ...defaultMenuStates, mobileMenu: !openMenus.mobileMenu });
  };

  const setDropdownMenuFromId = (dropdownId: string) => {
    const dropdown = businessContext.header?.navigationItems.find((item) => 'id' in item && item.id === dropdownId) as NormalizedNavigationDropdown;
    setDropdownMenu(dropdown);
  };

  const handleMenuClick = (state: { [key in AvailableMenus]?: boolean }, dropdownId?: string) => {
    setOpenMenus({ ...defaultMenuStates, ...state });

    if (state.shoppingCartMenu) {
      setExpandedState('right');
      return;
    }

    if ((!phone || (phone && !user)) && Object.values(state).some((value) => value)) {
      setExpandedState('right');

      return;
    }

    if (dropdownId) {
      setOpenMenus(defaultMenuStates);

      if (dropdownMenu?.id === dropdownId) {
        setExpandedState('none');

        return;
      }

      setDropdownMenuFromId(dropdownId);
      setExpandedState('left');

      return;
    }

    setExpandedState('none');
  };

  const handleClose = () => {
    if (!localStorage.getItem('new-nav-help-step')) localStorage.setItem('new-nav-help-step', 'INFO');
    if (openMenus.shoppingCartMenu) resetCartValidation();

    setOpenMenus(defaultMenuStates);
    setOpenDialogs(defaultDialogStates);
    setExpandedState('none');
  };

  const handleCustomerSwitchOpen = () => {
    handleClose();
    setOpenDialogs({ ...defaultDialogStates, customerSwitch: true });
  };

  const handleNavBack = () => {
    if (mobileNavStep === 'nav' && !dropdownMenu) handleClose();
    if (mobileNavStep !== 'nav') setMobileNavStep('nav');

    setDropdownMenu(undefined);
  };

  const countrylabel = useMemo(() => {
    const country = availableCountries.find((c) => c.shortName === getCountryCode(locale));

    return country?.fullName ?? '';
  }, [locale, availableCountries]);

  const languageLabel = useMemo(() => {
    const language = availableLanguages.find((c) => c.shortName === getLanguageCode(locale));

    return language?.fullName ?? '';
  }, [locale, availableLanguages]);

  const colorIndex = businessContext.header?.navigationItems.findIndex((item) => 'id' in item && item.id === dropdownMenu?.id) ?? 0;

  const onFirstLoginDismiss = () => {
    localStorage.setItem('new-nav-help-step', 'END');
    setIsFirstLogin(false);
    setFirstLoginChanged(true);
  };

  const navAlignment = useMemo(() => businessContext?.header?.alignment ?? 'Left', [businessContext]);

  useEffect(() => {
    setOpenMenus(defaultMenuStates);
    setDropdownMenu(undefined);
    setExpandedState('none');
  }, [desktop, tabLand, tabPort, phone]);

  useEffect(() => {
    document.documentElement.style.setProperty('--sticky-column-offset', user ? '180px' : '136px');
  }, []);

  useEffect(() => {
    if (expandedState === 'none' || expandedState === 'right') setTimeout(() => setDropdownMenu(undefined), 300);
  }, [expandedState]);

  useEffect(() => {
    if (user) {
      requestAnimationFrame(() => {
        setOpenMenus({
          ...defaultMenuStates,
          accountMenu: !phone && !localStorage.getItem('new-nav-help-step'),
          mobileAccountMenu: phone && !localStorage.getItem('new-nav-help-step')
        });
        setExpandedState(!phone && !localStorage.getItem('new-nav-help-step') ? 'right' : 'none');
      });
    }
  }, [user, phone, desktop]);

  useEffect(() => {
    if (firstLoginChanged && !phone) {
      setOpenMenus({ ...defaultMenuStates, accountMenu: true });
      setExpandedState('right');
      setFirstLoginChanged(false);
    }
  }, [openMenus]);

  useEffect(() => {
    setIsFirstLogin(!localStorage.getItem('new-nav-help-step') || localStorage.getItem('new-nav-help-step') === 'INFO');
  }, []);

  useEffect(() => {
    if (!accessToken) return;
    const shoppingCartType = shoppingCartState.type;
    if (shoppingCartType) {
      if (shoppingCartType === 'ADDED' || shoppingCartType === 'REMOVED' || shoppingCartType === 'RESET') {
        ShoppingCartUtils.getShoppingCart().then((shoppingCart) => {
          // make sure the menu opens as the badge is updated, avoid opening the menu before the badge is set
          setShoppingCartBadge(shoppingCart.numberOfItems);
          if (shoppingCartType === 'ADDED') {
            handleMenuClick({ shoppingCartMenu: true });
          }
        });
      } else {
        handleMenuClick({ shoppingCartMenu: true });
      }
    } else {
      handleMenuClick({ shoppingCartMenu: false });
    }
  }, [shoppingCartState]);

  useEffect(() => {
    if (queryParams.has('tech-knowledge-base')) {
      /**
       * Remove tech-knowledge-base query param from URL
       * to prevent the dialog from opening again on page refresh.
       */
      const params = new URLSearchParams(queryParams.toString());
      params.delete('tech-knowledge-base');

      router.replace(params.toString() ? `${path}?${params}` : path, { scroll: false });

      setOpenDialogs({
        ...defaultDialogStates,
        techKnowledgeBase: true
      });
    }
  }, [queryParams]);

  return (
    <>
      <Header
        sticky
        overflow="visible"
        onClose={handleClose}
        expanded={expandedState}
        container={!phone}
        toolbars={
          <NavBar
            size="large"
            showGutter={false}
            container={!phone}
            left={
              <Box height="100%" items="center" width="100%" spacing={{ left: 'lg' }}>
                <Link style={{ height: '100%' }} onClick={handleClose} href={`/${locale}`}>
                  <Box attributes={{ ...DataTestSelector('navbar-logo-link') }} height="100%" spacing={{ y: '2xs' }} items="center" maxWidth="164px">
                    <Image height="100%" objectFit="contain" src={AssetUtils.optimizeImageIfRequired(businessContext.logo.url, acceptWebp)} alt={businessContext.name} title={businessContext.name} />
                  </Box>
                </Link>

                <BreakpointSwitch includeViews={['phone', 'tab-port', 'tab-land']}>
                  <IconButton attributes={{ ...DataTestSelector('navbar-mobile-menu-trigger') }} icon={openMenus.mobileMenu ? 'times' : 'bars'} onClick={handleHamburgerClick} />
                </BreakpointSwitch>

                {(linkLegacyDigitalPlatform || (!linkLegacyDigitalPlatform && navAlignment === 'Left')) && (
                  <BreakpointSwitch includeViews={['desktop']}>
                    <Navigation businessContext={businessContext} locale={locale} dynamicPageUrls={dynamicPageUrls} onDropdownClick={(id) => handleMenuClick(defaultMenuStates, id)} currentDropdown={dropdownMenu} />
                  </BreakpointSwitch>
                )}
              </Box>
            }
            right={
              <Box items="center" gap="xs" height="100%" spacing={{ right: 'lg' }}>
                {linkLegacyDigitalPlatform && !isPublic && user && isFeatureEnabled(features, 'shopping-cart') && isCustomerPortal && (
                  <Box attributes={{ ...DataTestSelector('navbar-shopping-cart-link') }} items="center" gap="xs" height="100%">
                    <Link title={translate('page-consumer.header.shopping-cart')} href={`/${NavigationService.getLanguage(locale)}/my-shopping-cart`}>
                      <IconButton icon="shopping-cart" badge={shoppingCartBadge > 0 ? shoppingCartBadge.toString() : undefined} />
                    </Link>
                  </Box>
                )}

                {((linkLegacyDigitalPlatform && (availableCountries.length > 1 || availableLanguages.length > 1)) || (!linkLegacyDigitalPlatform && navAlignment === 'Left' && (availableCountries.length > 1 || availableLanguages.length > 1))) && (
                  <BreakpointSwitch includeViews={['desktop', 'tab-land']}>
                    <Box items="center" gap="xs" height="100%">
                      <IconButton attributes={{ ...DataTestSelector('navbar-country-language-trigger') }} icon="globe" onClick={() => handleMenuClick({ countryLanguageMenu: !openMenus.countryLanguageMenu })} />
                    </Box>
                  </BreakpointSwitch>
                )}

                {showLogin && (
                  <Box items="center" gap="xs" height="100%">
                    <AuthenticationButton
                      isOpen={openMenus.accountMenu || openMenus.loginMenu}
                      currentCustomer={currentCustomer}
                      onClick={() =>
                        handleMenuClick({
                          accountMenu: !phone && user && !openMenus.accountMenu,
                          mobileAccountMenu: phone && user && !openMenus.mobileAccountMenu,
                          loginMenu: !user && !openMenus.loginMenu
                        })
                      }
                    />
                  </Box>
                )}

                {!linkLegacyDigitalPlatform && navAlignment === 'Right' && (
                  <>
                    <BreakpointSwitch includeViews={['desktop']}>
                      <Navigation businessContext={businessContext} locale={locale} currentDropdown={dropdownMenu} dynamicPageUrls={dynamicPageUrls} onDropdownClick={(id) => handleMenuClick(defaultMenuStates, id)} />
                    </BreakpointSwitch>

                    {(availableCountries.length > 1 || availableLanguages.length > 1) && (
                      <BreakpointSwitch includeViews={['desktop', 'tab-land']}>
                        <Box items="center" gap="xs" height="100%">
                          <IconButton attributes={{ ...DataTestSelector('navbar-country-language-trigger') }} icon="globe" onClick={() => handleMenuClick({ countryLanguageMenu: !openMenus.countryLanguageMenu })} />
                        </Box>
                      </BreakpointSwitch>
                    )}
                  </>
                )}
              </Box>
            }
          />
        }
        leftCollapse={{
          body: <MegaMenu menu={dropdownMenu} onNavigate={handleClose} locale={locale} colorIndex={colorIndex} dynamicPageUrls={dynamicPageUrls} />,
          fullWidth: true
        }}
        rightCollapse={{
          body: (
            <>
              {openMenus.countryLanguageMenu && <CountryLanguageMenu locale={locale} availableCountries={availableCountries} availableLanguages={availableLanguages} localizedRoutes={localizedRoutes} />}
              {openMenus.loginMenu && <LoginMenu locale={locale} registrationLandingPage={businessContext.registrationLandingPage} />}
              {openMenus.accountMenu && (
                <ProfileMenu
                  locale={locale}
                  profileMenu={businessContext.profileMenu}
                  currentCustomer={currentCustomer}
                  isFirstLogin={isFirstLogin}
                  dynamicPageUrls={dynamicPageUrls}
                  onClick={() => handleMenuClick(defaultMenuStates)}
                  onCustomerSwitchClick={handleCustomerSwitchOpen}
                  onFirstLoginDismiss={onFirstLoginDismiss}
                />
              )}
              {openMenus.shoppingCartMenu && <ShoppingCartMenu locale={locale} />}
            </>
          ),
          fullWidth: phone
        }}
      />
      <BreakpointSwitch excludeViews={['desktop']}>
        <Flyout open={openMenus.mobileMenu && !desktop} onFlyoutClose={handleClose} position="left" closeOnOutsideClick>
          <FlyoutHeader onCloseClick={handleNavBack}>{mobileNavStep === 'nav' && dropdownMenu?.label}</FlyoutHeader>
          <FlyoutContent>
            {mobileNavStep === 'nav' && <Navigation businessContext={businessContext} locale={locale} dynamicPageUrls={dynamicPageUrls} onNavigate={() => handleMenuClick(defaultMenuStates)} onDropdownClick={setDropdownMenuFromId} currentDropdown={dropdownMenu} />}
            {mobileNavStep === 'countryAndLanguage' && <CountryLanguageMenu locale={locale} availableCountries={availableCountries} availableLanguages={availableLanguages} localizedRoutes={localizedRoutes} />}
          </FlyoutContent>
          {(availableCountries.length > 1 || availableLanguages.length > 1) && (
            <FlyoutActions>
              <Box attributes={{ ...DataTestSelector('navbar-mobile-country-language-trigger') }} height="44px" justify="between" items="center" onClick={() => setMobileNavStep('countryAndLanguage')}>
                <Box gap="sm">
                  <Icon icon="globe" color="strong" />
                  <Typography>
                    {countrylabel} / {languageLabel}
                  </Typography>
                </Box>
                <Icon icon="chevron-right" />
              </Box>
            </FlyoutActions>
          )}
        </Flyout>
        <Flyout open={openMenus.mobileAccountMenu && phone} onFlyoutClose={handleClose} position="right" closeOnOutsideClick>
          <FlyoutHeader>{translate('page-consumer.header.account.my-account')}</FlyoutHeader>
          <FlyoutContent>
            <ProfileMenu
              locale={locale}
              profileMenu={businessContext.profileMenu}
              currentCustomer={currentCustomer}
              isFirstLogin={isFirstLogin}
              dynamicPageUrls={dynamicPageUrls}
              onClick={() => handleMenuClick(defaultMenuStates)}
              onCustomerSwitchClick={handleCustomerSwitchOpen}
              onFirstLoginDismiss={onFirstLoginDismiss}
            />
          </FlyoutContent>
        </Flyout>
      </BreakpointSwitch>
      {user && <CustomerSwitchDialog open={openDialogs.customerSwitch} currentCustomer={currentCustomer} onDialogClose={() => setOpenDialogs(defaultDialogStates)} />}
      <TechKnowledgeBaseDialog open={openDialogs.techKnowledgeBase} data={technicalSupport} onDialogClose={() => setOpenDialogs(defaultDialogStates)} />
    </>
  );
};

export default Navbar;
